import { useEffect, useState, useCallback } from 'react';

/**
 * Observe the size of an HTML element via ResizeObserver.
 *
 * Returns a ref-callback to attach to the target element and the current
 * `{ width, height }` of that element (or `null` before first measurement).
 *
 * Uses a ref-callback (not a useRef) so dimensions are re-measured when the
 * target node is replaced (e.g. conditional rendering).
 *
 * @returns {[(node: HTMLElement | null) => void, { width: number, height: number } | null]}
 */
export const useElementSize = () => {
  const [size, setSize] = useState(null);
  const [node, setNode] = useState(null);

  const ref = useCallback((newNode) => {
    setNode(newNode);
  }, []);

  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const rect = node.getBoundingClientRect();
      setSize((prev) => {
        if (prev && prev.width === rect.width && prev.height === rect.height) {
          return prev;
        }
        return { width: rect.width, height: rect.height };
      });
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  return [ref, size];
};
